import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import {
  Box,
  Button,
  IconButton,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  styled,
  Select,
  Stack,
  TextField,
  Typography,
  createTheme,
  useMediaQuery,
} from '@mui/material';
import './style.css';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { enIN } from 'date-fns/locale';
import LeadStore from '../../store/LeadStore';
import dayjs from 'dayjs';
import { isEmpty } from '../../utils/helper';
import { Delete } from '@mui/icons-material';
import MultiSelectDropdown from './MultiSelectDropdown';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export const EditClientPersonalInfo = observer(({ leadId, details, handleEditDetails }) => {
  const theme = createTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const CustomButton = styled(Button)({
    backgroundColor: '#F07B0E',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#f1785a',
    },
    width: 'fit-content',
    alignSelf: isSmallScreen ? 'center' : 'end',
  });

  const constructNewChildRow = (id) => {
    return {
      rowId: id,
      name: null,
      dob: null,
      gender: null,
      age: null,
    };
  };

  const [formChildren, setFormChildren] = useState(details?.children ? JSON.parse(details?.children) : []);
  const [formData, setFormData] = useState({
    id: details?.clientPersonalInfoId ?? null,
    leadId,
    numberOfPersonsStayInHome: details?.numberOfPersonsStayInHome,
    community: details?.community,
    communityOther: details?.communityOther,
    typeOfFamily: details?.typeOfFamily,
    typeOfFamilyOther: details?.typeOfFamilyOther,
    children: details?.children ? JSON.parse(details?.children) : [],
    clientMotherToungh: details?.clientMotherToungh,
    clientMotherTounghOther: details?.clientMotherTounghOther,
    clientPreferedLanguageInMeeting: details?.clientPreferedLanguageInMeeting,
    clientPreferedLanguageInMeetingOther: details?.clientPreferedLanguageInMeetingOther,
    clientModeOfTransportation: details?.clientModeOfTransportation,
    anniversaryDate: details?.anniversaryDate,
    husbandDob: details?.husbandDob,
    wifeDob: details?.wifeDob,
    favoritePlaceInMysore: details?.favoritePlaceInMysore,
    favoritePlaceInKarnataka: details?.favoritePlaceInKarnataka,
    favoritePlaceInIndia: details?.favoritePlaceInIndia,
    favoritePlaceInWorld: details?.favoritePlaceInWorld,
    preferedDrink: details?.preferedDrink,
    clientOccupation: details?.clientOccupation,
    clientBusinessName: details?.clientOccupationBusinessName,
    clientWorkingCompany: details?.clientWorkingCompany,
    clientDesignation: details?.clientDesignation,
    createdBy: details?.CreatedBy,
    createdDate: details?.createdDate,
    updatedBy: details?.updatedBy,
    updatedDate: details?.updatedDate,
  });

  const [errors, setErrors] = useState({
    numberOfPersonsStayInHome: false,
    community: false,
    communityOther: false,
    typeOfFamily: false,
    typeOfFamilyOther: false,
    clientMotherToungh: false,
    clientMotherTounghOther: false,
    clientPreferedLanguageInMeeting: false,
    clientPreferedLanguageInMeetingOther: false,
    clientModeOfTransportation: false,
    anniversaryDate: false,
    husbandDob: false,
    wifeDob: false,
    favoritePlaceInMysore: false,
    favoritePlaceInKarnataka: false,
    favoritePlaceInIndia: false,
    favoritePlaceInWorld: false,
    preferedDrink: false,
    clientOccupation: false,
    clientBusinessName: false,
    clientWorkingCompany: false,
    clientDesignation: false,
  });

  const [errorMessages, setErrorMessages] = useState({
    numberOfPersonsStayInHome: '',
    community: '',
    communityOther: '',
    typeOfFamily: '',
    typeOfFamilyOther: '',
    clientMotherToungh: '',
    clientMotherTounghOther: '',
    clientPreferedLanguageInMeeting: '',
    clientPreferedLanguageInMeetingOther: '',
    clientModeOfTransportation: '',
    anniversaryDate: '',
    husbandDob: '',
    wifeDob: '',
    favoritePlaceInMysore: '',
    favoritePlaceInKarnataka: '',
    favoritePlaceInIndia: '',
    favoritePlaceInWorld: '',
    preferedDrink: '',
    clientOccupation: '',
    clientBusinessName: '',
    clientWorkingCompany: '',
    clientDesignation: '',
  });

  const communityList = [
    { id: 1, name: 'Hindus' },
    { id: 2, name: 'Muslims' },
    { id: 3, name: 'Christians' },
    { id: 4, name: 'Sikhs' },
    { id: 5, name: 'Jains' },
    { id: 6, name: 'Buddhists' },
    { id: 999, name: 'Other' },
  ];

  const typeOfFamilyList = [
    { id: 1, name: 'Nuclear Family' },
    { id: 2, name: 'Old Aged Couple' },
    { id: 3, name: 'Newly Married Couple' },
    { id: 4, name: 'Joint Family' },
    { id: 5, name: 'Brothers/Sister/Friends' },
    { id: 999, name: 'Other' },
  ];

  const languagesList = [
    { id: 1, name: 'Kannada' },
    { id: 2, name: 'English' },
    { id: 3, name: 'Tamil' },
    { id: 4, name: 'Telugu' },
    { id: 5, name: 'Malayalam' },
    { id: 6, name: 'Urdu' },
    { id: 7, name: 'Tulu' },
    { id: 8, name: 'Hindi' },
    { id: 9, name: 'Marati' },
    { id: 999, name: 'Other' },
  ];

  const clientModeOfTransportationList = [
    { id: 1, name: '2 Wheeler' },
    { id: 2, name: '4 Wheeler' },
    { id: 3, name: 'Auto' },
    { id: 4, name: 'Public Transport' },
    { id: 5, name: 'None' },
  ];

  const preferedDrinkList = [
    { id: 1, name: 'Alcoholic' },
    { id: 2, name: 'Non-Alcoholic' },
  ];

  const clientOccupationList = [
    { id: 1, name: 'Business' },
    { id: 2, name: 'Employed' },
    { id: 3, name: 'Self Employee' },
    { id: 4, name: 'Trader' },
    { id: 5, name: 'Investor' },
  ];

  const genderList = [
    { id: 1, name: 'Male' },
    { id: 2, name: 'Female' },
    { id: 3, name: 'Other' },
  ];

  const getNumberOfPersonsStayInHomeList = () => {
    const list = [];
    for (let index = 1; index <= 20; index++) {
      list.push({
        id: index,
        name: index,
      });
    }

    return list;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Perform validation
    if (name === 'contactNumber') {
      setErrors({ ...errors, [name]: isContactNumberError(value, name) });
    } else if (name === 'alternativeNumber') {
      setErrors({ ...errors, [name]: isNumberError(value, name) });
    } else {
      setErrors({ ...errors, [name]: isEmpty(value) });
    }
  };

  const handleChildChange = (child, name, value) => {
    const selectedRowIndex = formChildren.findIndex((item) => item.rowId.toString() === child.rowId.toString());

    const childrenLocal = [...formChildren];
    childrenLocal[selectedRowIndex][name] = value;
    if (name === 'dob') {
      const dateDiff = new Date() - value;
      childrenLocal[selectedRowIndex].age = Math.floor(dateDiff / (1000 * 60 * 60 * 24 * 365));
    }

    setFormChildren([...childrenLocal]);
  };

  const handleNewChild = () => {
    const childrenLocal = [...formChildren];
    childrenLocal.push(constructNewChildRow(formChildren.length + 1));
    setFormChildren([...childrenLocal]);
  };

  const handleDeleteChildRow = (rowId) => {
    setFormChildren(formChildren.filter((item) => item.rowId.toString() !== rowId.toString()));
  };

  const handleUpdateLeadKycSubmit = (e) => {
    e.preventDefault();
    const updatedErrors = {
      communityOther: formData.community === 'Other' && isEmpty(formData.communityOther),
      typeOfFamilyOther: formData.typeOfFamily === 'Other' && isEmpty(formData.typeOfFamilyOther),
      clientMotherTounghOther: formData.clientMotherToungh === 'Other' && isEmpty(formData.clientMotherTounghOther),
      clientPreferedLanguageInMeeting:
        formData.clientPreferedLanguageInMeeting === 'Other' && isEmpty(formData.clientPreferedLanguageInMeetingOther),
      clientBusinessName: formData.clientOccupation === 'Business' && isEmpty(formData.clientBusinessName),
      clientWorkingCompany: formData.clientOccupation === 'Employed' && isEmpty(formData.clientWorkingCompany),
      clientDesignation: formData.clientOccupation === 'Employed' && isEmpty(formData.clientDesignation),
    };
    setErrors(updatedErrors);

    if (!Object.values(updatedErrors).some((error) => error === true)) {
      const payload = {
        ...formData,
        children: JSON.stringify(formChildren),
      };
      isEmpty(formData.id)
        ? LeadStore.createClientPersonalDetails(payload)
        : LeadStore.updateClientPersonalDetails(payload);
    }
  };

  useEffect(() => {
    LeadStore.updateClientPersonalDetailsStatus && handleEditDetails();
  }, [LeadStore.updateClientPersonalDetailsStatus]);

  const isContactNumberError = (value, name) => {
    if (isEmpty(value)) {
      return true;
    } else if (!/^\d{0,10}$/.test(value)) {
      setErrorMessages({
        ...errors,
        [name]: 'Please enter valid 10 digit number',
      });
      return true;
    } else {
      return false;
    }
  };

  const isNumberError = (value, name) => {
    if (isEmpty(value)) {
      return false;
    } else if (!/^\d{0,10}$/.test(value)) {
      setErrorMessages({
        ...errors,
        [name]: 'Please enter valid 10 digit number',
      });
      return true;
    } else {
      return false;
    }
  };

  return (
    <Paper sx={{ px: '30px', py: '20px', pb: '40px' }}>
      <Box
        id="lead-kyc-details-form"
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={(event) => {
          event.preventDefault();
          handleUpdateLeadKycSubmit(event);
        }}
      >
        <Stack spacing={4}>
          <Stack spacing={3}>
            <Typography sx={{ fontWeight: '500', alignSelf: 'start' }} component="p">
              Client Personal Details
            </Typography>
            <Grid rowGap={2} container>
              <Grid item xs={12} sm={6} md={6} lg={6} sx={{ pr: isSmallScreen ? '0px' : '10px' }}>
                <Stack direction={'column'} spacing={3}>
                  <FormControl variant="standard" error={Boolean(errors.numberOfPersonsStayInHome)}>
                    <InputLabel id="numberOfPersonsStayInHomeLabel" shrink={true}>
                      Number of person's stay in the Home
                    </InputLabel>
                    <Select
                      labelId="numberOfPersonsStayInHomeLabel"
                      id="numberOfPersonsStayInHome"
                      name="numberOfPersonsStayInHome"
                      value={formData.numberOfPersonsStayInHome ?? ''}
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;
                        setFormData({
                          ...formData,
                          numberOfPersonsStayInHome: value,
                        });
                        setErrors({
                          ...errors,
                          numberOfPersonsStayInHome: isEmpty(value),
                        });
                      }}
                      MenuProps={MenuProps}
                    >
                      {getNumberOfPersonsStayInHomeList().map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl variant="standard" error={Boolean(errors.community)}>
                    <InputLabel id="communityLabel" shrink={true}>
                      Community
                    </InputLabel>
                    <Select
                      labelId="communityLabel"
                      id="community"
                      name="community"
                      value={formData.community ?? ''}
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;
                        setFormData({
                          ...formData,
                          community: value,
                          communityOther: value !== 'Other' ? null : formData.communityOther,
                        });
                        setErrors({ ...errors, community: isEmpty(value) });
                      }}
                      MenuProps={MenuProps}
                    >
                      {communityList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {formData.community === 'Other' && (
                    <TextField
                      label="Community Other"
                      id="communityOther"
                      size="small"
                      variant="standard"
                      required={formData.community === 'Other'}
                      name="communityOther"
                      multiline
                      rows={1}
                      value={formData.communityOther ?? ''}
                      onChange={(e) => handleChange(e)}
                      error={Boolean(errors.communityOther)}
                      sx={{ fontSize: '12px' }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  <FormControl variant="standard" error={Boolean(errors.typeOfFamily)}>
                    <InputLabel id="typeOfFamilyLabel" shrink={true}>
                      Type of Family
                    </InputLabel>
                    <Select
                      labelId="typeOfFamilyLabel"
                      id="typeOfFamily"
                      name="typeOfFamily"
                      value={formData.typeOfFamily ?? ''}
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;
                        setFormData({
                          ...formData,
                          typeOfFamily: value,
                          typeOfFamilyOther: value !== 'Other' ? null : formData.typeOfFamilyOther,
                        });
                        setErrors({
                          ...errors,
                          typeOfFamily: isEmpty(value),
                        });
                      }}
                      MenuProps={MenuProps}
                    >
                      {typeOfFamilyList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {formData.typeOfFamily === 'Other' && (
                    <TextField
                      label="Type of Family Other"
                      id="typeOfFamilyOther"
                      size="small"
                      variant="standard"
                      required={formData.typeOfFamily === 'Other'}
                      name="typeOfFamilyOther"
                      multiline
                      rows={1}
                      value={formData.typeOfFamilyOther ?? ''}
                      onChange={(e) => handleChange(e)}
                      error={Boolean(errors.typeOfFamilyOther)}
                      sx={{ fontSize: '12px' }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  <MultiSelectDropdown
                    data={{
                      label: 'Client mother Toungh',
                      required: false,
                      allowFreeField: true, // Allows users to enter their own values
                      allowMultipleSelection: true, // Multiple selections allowed
                      options: [
                        { label: 'Kannada', required: false, options: [] },
                        { label: 'English', required: false, options: [] },
                        { label: 'Tamil', required: false, options: [] },
                        { label: 'Telugu', required: false, options: [] },
                        { label: 'Malayalam', required: false, options: [] },
                        { label: 'Urdu', required: false, options: [] },
                        { label: 'Tulu', required: false, options: [] },
                        { label: 'Hindi', required: false, options: [] },
                        { label: 'Marati', required: false, options: [] },
                      ],
                    }}
                    name="clientMotherToungh"
                    value={formData.clientMotherToungh}
                    onChange={handleChange}
                    error={Boolean(errors.clientMotherToungh)}
                  />

                  {/* <FormControl variant="standard" error={Boolean(errors.clientMotherToungh)}>
                    <InputLabel id="clientMotherTounghLabel" shrink={true}>Client mother Toungh</InputLabel>
                    <Select
                      labelId="clientMotherTounghLabel"
                      id="clientMotherToungh"
                      name="clientMotherToungh"
                      value={formData.clientMotherToungh ?? ''}
                      onChange={e => {
                        const { target: { value } } = e;
                        setFormData({ ...formData,
                          'clientMotherToungh': value,
                          'clientMotherTounghOther': value !== 'Other' ? null : formData.clientMotherTounghOther
                        })
                        setErrors({ ...errors, 'clientMotherToungh': isEmpty(value)})
                      }}
                      MenuProps={MenuProps}
                    >
                      {languagesList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl> */}
                  {/* {
                    formData.clientMotherToungh === 'Other' &&
                    <TextField
                      label='Client Mother Toungh Other'
                      id="clientMotherTounghOther"
                      size="small"
                      variant="standard"
                      required={formData.clientMotherToungh === 'Other'}
                      name="clientMotherTounghOther"
                      multiline
                      rows={1}
                      value={formData.clientMotherTounghOther ?? ''}
                      onChange={e => handleChange(e)}
                      error={Boolean(errors.clientMotherTounghOther)}
                      sx={{fontSize: '12px'}}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  } */}
                  <MultiSelectDropdown
                    data={{
                      label: 'Client Prefered Language in Meeting',
                      required: false,
                      allowFreeField: true, // Allows users to enter their own values
                      allowMultipleSelection: true, // Multiple selections allowed
                      options: [
                        { label: 'Kannada', required: false, options: [] },
                        { label: 'English', required: false, options: [] },
                        { label: 'Tamil', required: false, options: [] },
                        { label: 'Telugu', required: false, options: [] },
                        { label: 'Malayalam', required: false, options: [] },
                        { label: 'Urdu', required: false, options: [] },
                        { label: 'Tulu', required: false, options: [] },
                        { label: 'Hindi', required: false, options: [] },
                        { label: 'Marati', required: false, options: [] },
                      ],
                    }}
                    name="clientPreferedLanguageInMeeting"
                    value={formData.clientPreferedLanguageInMeeting}
                    onChange={handleChange}
                    error={Boolean(errors.clientPreferedLanguageInMeeting)}
                  />
                  {/* <FormControl variant="standard" error={Boolean(errors.clientPreferedLanguageInMeeting)}>
                    <InputLabel id="clientPreferedLanguageInMeetingLabel" shrink={true}>Client Prefered Language in Meeting</InputLabel>
                    <Select
                      labelId="clientPreferedLanguageInMeetingLabel"
                      id="clientPreferedLanguageInMeeting"
                      name="clientPreferedLanguageInMeeting"
                      value={formData.clientPreferedLanguageInMeeting ?? ''}
                      onChange={e => {
                        const { target: { value } } = e;
                        setFormData({ ...formData,
                          ['clientPreferedLanguageInMeeting']: value,
                          ['clientPreferedLanguageInMeetingOther']: value !== 'Other' ? null : formData.clientPreferedLanguageInMeetingOther
                        })
                        setErrors({ ...errors, ['clientPreferedLanguageInMeeting']: isEmpty(value)})
                      }}
                      MenuProps={MenuProps}
                    >
                      {languagesList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {
                    formData.clientPreferedLanguageInMeeting === 'Other' &&
                    <TextField
                      label='Client Mother Toungh Other'
                      id="clientPreferedLanguageInMeetingOther"
                      size="small"
                      variant="standard"
                      required={formData.clientPreferedLanguageInMeeting === 'Other'}
                      name="clientPreferedLanguageInMeetingOther"
                      multiline
                      rows={1}
                      value={formData.clientPreferedLanguageInMeetingOther ?? ''}
                      onChange={e => handleChange(e)}
                      error={Boolean(errors.clientPreferedLanguageInMeetingOther)}
                      sx={{fontSize: '12px'}}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  } */}
                  <MultiSelectDropdown
                    data={{
                      label: 'Client Mode of Transportation',
                      required: false,
                      allowFreeField: true, // Allows users to enter their own values
                      allowMultipleSelection: true, // Multiple selections allowed
                      options: [
                        { label: '2 Wheeler', required: false, options: [] },
                        { label: '4 Wheeler', required: false, options: [] },
                        { label: 'Auto', required: false, options: [] },
                        {
                          label: 'Public Transport',
                          required: false,
                          options: [],
                        },
                        { label: 'None', required: false, options: [] },
                      ],
                    }}
                    name="clientModeOfTransportation"
                    value={formData.clientModeOfTransportation}
                    onChange={handleChange}
                    error={Boolean(errors.clientModeOfTransportation)}
                  />
                  {/* <FormControl variant="standard" error={Boolean(errors.clientModeOfTransportation)}>
                    <InputLabel id="clientModeOfTransportationLabel" shrink={true}>Client Mode of Transportation</InputLabel>
                    <Select
                      labelId="clientModeOfTransportationLabel"
                      id="clientModeOfTransportation"
                      name="clientModeOfTransportation"
                      value={formData.clientModeOfTransportation ?? ''}
                      onChange={e => {
                        const { target: { value } } = e;
                        setFormData({ ...formData,
                          'clientModeOfTransportation': value
                        })
                        setErrors({ ...errors, 'clientModeOfTransportation': isEmpty(value)})
                      }}
                      MenuProps={MenuProps}
                    >
                      {clientModeOfTransportationList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl> */}
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6} sx={{ pl: isSmallScreen ? '0px' : '10px' }}>
                <Stack direction={'column'} spacing={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
                    <DatePicker
                      label="Anniversary Date"
                      value={formData.anniversaryDate ? new Date(formData.anniversaryDate) : null}
                      onChange={(newValue) => {
                        handleChange({
                          target: {
                            name: 'anniversaryDate',
                            value: newValue,
                          },
                        });
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          variant: 'standard',
                          name: 'anniversaryDate',
                          fontSize: '12px',
                          error: Boolean(errors.anniversaryDate),
                          InputLabelProps: { shrink: true },
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
                    <DatePicker
                      label="Husband DOB"
                      value={formData.husbandDob ? new Date(formData.husbandDob) : null}
                      onChange={(newValue) => {
                        handleChange({
                          target: { name: 'husbandDob', value: newValue },
                        });
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          variant: 'standard',
                          name: 'husbandDob',
                          fontSize: '12px',
                          error: Boolean(errors.husbandDob),
                          InputLabelProps: { shrink: true },
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
                    <DatePicker
                      label="Wife DOB"
                      value={formData.wifeDob ? new Date(formData.wifeDob) : null}
                      onChange={(newValue) => {
                        handleChange({
                          target: { name: 'wifeDob', value: newValue },
                        });
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          variant: 'standard',
                          name: 'wifeDob',
                          fontSize: '12px',
                          error: Boolean(errors.wifeDob),
                          InputLabelProps: { shrink: true },
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <FormControl variant="standard" error={Boolean(errors.preferedDrink)}>
                    <InputLabel id="preferedDrinkLabel" shrink={true}>
                      Prefered Drink
                    </InputLabel>
                    <Select
                      labelId="preferedDrinkLabel"
                      id="preferedDrink"
                      name="preferedDrink"
                      value={formData.preferedDrink ?? ''}
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;
                        setFormData({ ...formData, preferedDrink: value });
                        setErrors({
                          ...errors,
                          preferedDrink: isEmpty(value),
                        });
                      }}
                      MenuProps={MenuProps}
                    >
                      {preferedDrinkList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl variant="standard" error={Boolean(errors.clientOccupation)}>
                    <InputLabel id="clientOccupationLabel" shrink={true}>
                      Client Occupation
                    </InputLabel>
                    <Select
                      labelId="clientOccupationLabel"
                      id="clientOccupation"
                      name="clientOccupation"
                      value={formData.clientOccupation ?? ''}
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;
                        setFormData({ ...formData, clientOccupation: value });
                        setErrors({
                          ...errors,
                          clientOccupation: isEmpty(value),
                        });
                      }}
                      MenuProps={MenuProps}
                    >
                      {clientOccupationList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {formData.clientOccupation === 'Business' && (
                    <TextField
                      label="Client Business Name"
                      id="clientBusinessName"
                      size="small"
                      variant="standard"
                      required={formData.clientOccupation === 'Business'}
                      name="clientBusinessName"
                      multiline
                      rows={1}
                      value={formData.clientBusinessName ?? ''}
                      onChange={(e) => handleChange(e)}
                      error={Boolean(errors.clientBusinessName)}
                      sx={{ fontSize: '12px' }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  {formData.clientOccupation === 'Employed' && (
                    <TextField
                      label="Client Working Company"
                      id="clientWorkingCompany"
                      size="small"
                      variant="standard"
                      required={formData.clientOccupation === 'Employed'}
                      name="clientWorkingCompany"
                      multiline
                      rows={1}
                      value={formData.clientWorkingCompany ?? ''}
                      onChange={(e) => handleChange(e)}
                      error={Boolean(errors.clientWorkingCompany)}
                      sx={{ fontSize: '12px' }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  {formData.clientOccupation === 'Employed' && (
                    <TextField
                      label="Client Designation"
                      id="clientDesignation"
                      size="small"
                      variant="standard"
                      required={formData.clientOccupation === 'Employed'}
                      name="clientDesignation"
                      multiline
                      rows={1}
                      value={formData.clientDesignation ?? ''}
                      onChange={(e) => handleChange(e)}
                      error={Boolean(errors.clientDesignation)}
                      sx={{ fontSize: '12px' }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Stack>
          <Stack spacing={3}>
            <Stack direction={'row'}>
              <Typography sx={{ fontWeight: '500', alignSelf: 'start' }} component="p">
                Children
              </Typography>
              <CustomButton sx={{ ml: 'auto' }} variant="contained" size="small" onClick={handleNewChild}>
                Add
              </CustomButton>
            </Stack>
            <Grid rowGap={2} container>
              <Grid item xs={12} sm={6} md={6} lg={6} sx={{ pr: isSmallScreen ? '0px' : '10px' }}>
                <Stack direction={'column'} spacing={3}>
                  {formChildren.map((child, index) => {
                    return (
                      <>
                        <Stack direction={'row'}>
                          <Typography
                            sx={{
                              fontWeight: '500',
                              alignSelf: 'start',
                              fontSize: '0.75rem',
                              marginBottom: '1rem',
                            }}
                            component="p"
                          >
                            Child {index + 1}
                          </Typography>
                          {isSmallScreen && (
                            <IconButton
                              aria-label="delete"
                              size="small"
                              sx={{ ml: 'auto' }}
                              onClick={() => handleDeleteChildRow(child.rowId)}
                              color="error"
                            >
                              <Delete />
                            </IconButton>
                          )}
                        </Stack>
                        <TextField
                          label="Name"
                          id="name"
                          size="small"
                          variant="standard"
                          name="name"
                          sx={{ fontSize: '12px' }}
                          value={child.name ?? ''}
                          onChange={(e) => {
                            handleChildChange(child, 'name', e.target.value);
                          }}
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                        <FormControl variant="standard">
                          <InputLabel id="genderLabel" shrink={true}>
                            Gender
                          </InputLabel>
                          <Select
                            labelId="genderLabel"
                            id="gender"
                            name="gender"
                            value={child.gender ?? ''}
                            onChange={(e) => {
                              handleChildChange(child, 'gender', e.target.value);
                            }}
                            MenuProps={MenuProps}
                          >
                            {genderList.map((site) => (
                              <MenuItem key={site.id} value={site.name}>
                                <ListItemText primary={site.name} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </>
                    );
                  })}
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6} sx={{ pl: isSmallScreen ? '0px' : '10px' }}>
                <Stack direction={'column'} spacing={3}>
                  {formChildren.map((child) => {
                    return (
                      <>
                        {!isSmallScreen && (
                          <IconButton
                            aria-label="delete"
                            size="small"
                            sx={{ alignSelf: 'end' }}
                            onClick={() => handleDeleteChildRow(child.rowId)}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        )}
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
                          <DatePicker
                            label="DOB"
                            value={formData.dob ? new Date(formData.dob) : null}
                            onChange={(newValue) => {
                              handleChildChange(child, 'dob', newValue);
                            }}
                            slotProps={{
                              textField: {
                                size: 'small',
                                variant: 'standard',
                                name: 'dob',
                                fontSize: '12px',
                                InputLabelProps: { shrink: true },
                              },
                            }}
                          />
                        </LocalizationProvider>
                        <TextField
                          label="Age"
                          id="age"
                          size="small"
                          variant="standard"
                          name="age"
                          disabled
                          sx={{ fontSize: '12px' }}
                          value={child.age ?? ''}
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </>
                    );
                  })}
                </Stack>
              </Grid>
            </Grid>
          </Stack>
          <Stack spacing={3}>
            <Typography sx={{ fontWeight: '500', alignSelf: 'start' }} component="p">
              Favorite Place
            </Typography>
            <Grid rowGap={2} container>
              <Grid item xs={12} sm={6} md={6} lg={6} sx={{ pr: isSmallScreen ? '0px' : '10px' }}>
                <Stack direction={'column'} spacing={3}>
                  <TextField
                    label="In Mysore"
                    id="favoritePlaceInMysore"
                    size="small"
                    variant="standard"
                    name="favoritePlaceInMysore"
                    sx={{ fontSize: '12px' }}
                    value={formData.favoritePlaceInMysore ?? ''}
                    onChange={handleChange}
                    error={Boolean(errors.favoritePlaceInMysore)}
                    helperText={Boolean(errors.favoritePlaceInMysore) && errorMessages.favoritePlaceInMysore}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    label="In Karnataka"
                    id="favoritePlaceInKarnataka"
                    size="small"
                    variant="standard"
                    name="favoritePlaceInKarnataka"
                    sx={{ fontSize: '12px' }}
                    value={formData.favoritePlaceInKarnataka ?? ''}
                    onChange={handleChange}
                    error={Boolean(errors.favoritePlaceInKarnataka)}
                    helperText={Boolean(errors.favoritePlaceInKarnataka) && errorMessages.favoritePlaceInKarnataka}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6} sx={{ pl: isSmallScreen ? '0px' : '10px' }}>
                <Stack direction={'column'} spacing={3}>
                  <TextField
                    label="In India"
                    id="favoritePlaceInIndia"
                    size="small"
                    variant="standard"
                    name="favoritePlaceInIndia"
                    sx={{ fontSize: '12px' }}
                    value={formData.favoritePlaceInIndia ?? ''}
                    onChange={handleChange}
                    error={Boolean(errors.favoritePlaceInIndia)}
                    helperText={Boolean(errors.favoritePlaceInIndia) && errorMessages.favoritePlaceInIndia}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    label="In World"
                    id="favoritePlaceInWorld"
                    size="small"
                    variant="standard"
                    name="favoritePlaceInWorld"
                    sx={{ fontSize: '12px' }}
                    value={formData.favoritePlaceInWorld ?? ''}
                    onChange={handleChange}
                    error={Boolean(errors.favoritePlaceInWorld)}
                    helperText={Boolean(errors.favoritePlaceInWorld) && errorMessages.favoritePlaceInWorld}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Stack>
          <Stack direction={'row'} sx={{ ml: 'auto' }} alignSelf={isSmallScreen ? '' : 'end'} spacing={2}>
            <Button variant="outlined" color="inherit" fullWidth size="small" onClick={() => handleEditDetails(false)}>
              Cancel
            </Button>
            <Button variant="contained" type="submit" fullWidth color="primary" size="small">
              Update
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
});
